import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import baseURL from '../../config';
import {
  Box,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 450,
  maxHeight: 500,
  overflowY: 'auto',
  bgcolor: 'background.paper',
  border: '2px solid #1976d2',
  boxShadow: 24,
  p: 4,
};

const BsmContracts = () => {
  const { id: vesselownerId } = useParams();
  const [contracts, setContracts] = useState([]);
  const [contractCount, setContractCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modalData, setModalData] = useState({ type: null, files: [] });

  useEffect(() => {
    const fetchBsmContractData = async () => {
      if (!vesselownerId) {
        setError('Vessel Owner ID is missing.');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Token is missing.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${baseURL}/api/vesselOwner/getcontractforvesselownerafterForm1/${vesselownerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response?.data) {
          setContracts(response.data?.data || []);
          setContractCount(response.data?.totalCount || 0);
        } else {
          setError('No data found for the given vessel owner.');
        }
      } catch (error) {
        console.error('Error fetching contract data:', error);
        setError('Failed to fetch contract data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchBsmContractData();
  }, [vesselownerId]);

  const handleOpenModal = (type, files) => {
    setModalData({ type, files });
  };

  const handleCloseModal = () => {
    setModalData({ type: null, files: [] });
  };

  const getFileLink = (fileObj) => {
    if (!fileObj?.filePath) return null;
    return {
      url: `${baseURL}/${fileObj.filePath.replace(/\\/g, '/')}`,
      name: fileObj.originalName,
    };
  };

  const formatLabel = (label) =>
    label.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');

  const extractFiles = (sourceObj) =>
    Object.entries(sourceObj || {})
      .filter(([_, val]) => val?.filePath && val?.originalName)
      .map(([key, val]) => ({
        label: formatLabel(key),
        link: getFileLink(val),
      }));

  const extractDocFiles = (contract) => {
    const excludedKeys = [
      'ShipDetails',
      'crewdetails',
      'vesselOwner',
      'crewingAgent',
      '__v',
      '_id',
      'latestStatus',
      'statusHistory',
      'formI',
      'createdAt',
      'creatorIdWithName',
      'crewName',
      'rank',
      'rankName',
      'vesselName',
      'vesselOwnerName',
      'isApproved',
    ];

    return Object.entries(contract || {})
      .filter(([key, val]) => !excludedKeys.includes(key) && val?.filePath && val?.originalName)
      .map(([key, val]) => ({
        label: formatLabel(key),
        link: getFileLink(val),
      }));
  };

  const renderModal = () => {
    if (!modalData.type) return null;
  
    return (
      <Modal open={true} onClose={handleCloseModal}>
        <Box
          sx={{
            ...modalStyle,
            width: '60%',  // Adjust the width to your preferred size
            maxWidth: '600px',  // Optional: max width to prevent it from being too wide
            overflow: 'hidden', // Remove the scrollbar
          }}
        >
          <Typography variant="h6" mb={3} textAlign="center">
            {modalData.type === 'vessel' && 'Vessel Documents'}
            {modalData.type === 'crew' && 'Crew Documents'}
            {modalData.type === 'docs' && 'Contract Documents'}
          </Typography>
  
          {modalData.files.length > 0 ? (
            modalData.files.map(({ label, link }, idx) =>
              link ? (
                <Box
                  key={idx}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  bgcolor="#fff"
                  boxShadow={2}
                  borderRadius={2}
                  px={2}
                  py={1.5}
                  mb={2}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/337/337946.png"
                      alt="PDF Icon"
                      width={24}
                      height={24}
                    />
                    <Typography fontWeight="500">{label}</Typography>
                  </Box>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="contained" size="small" color="primary">
                      Download
                    </Button>
                  </a>
                </Box>
              ) : null
            )
          ) : (
            <Typography>No documents available.</Typography>
          )}
  
          <Box textAlign="center" mt={3}>
            <Button variant="contained" color="error" onClick={handleCloseModal}>
              CLOSE
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };
  
  

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" mb={2}>BSM Contracts</Typography>
      <Typography variant="subtitle1" mb={2}>Total Contracts: {contractCount}</Typography>

      {contracts.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1976d2' }}>
              <TableCell sx={{ color: 'white' }}>Sr. No.</TableCell>
              <TableCell sx={{ color: 'white' }}>Vessel Owner</TableCell>
              <TableCell sx={{ color: 'white' }}>Crew</TableCell>
              <TableCell sx={{ color: 'white' }}>Crewing Agent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contracts.map((item, index) => {
              const contract = item.contractId;
              const shipDetails = contract?.ShipDetails || {};
              const crewDetails = contract?.crewdetails || {};

              return (
                <TableRow key={item._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <strong>Company:</strong> {contract?.vesselOwner?.company_shortname}<br />
                    <strong>Vessel:</strong> {contract?.vesselName}<br />
                    <strong>Contract Created:</strong> {new Date(item.createdAt).toLocaleString()}<br />
                    <Button
                      variant="contained"
                      
                      sx={{ mt: 1 }}
                      onClick={() => {
                        const vesselFiles = extractFiles(shipDetails);
                        handleOpenModal('vessel', vesselFiles);
                      }}
                    >
                      VESSEL FILES
                    </Button>
                  </TableCell>
                  <TableCell>
                    <strong>Name:</strong> {crewDetails?.name}<br />
                    <strong>Indos:</strong> {crewDetails?.indosno}<br />
                    <strong>Email:</strong> {crewDetails?.email}<br />
                    <strong>Rank:</strong> {crewDetails?.rankname}<br />
                    <Button
                      variant="contained"
                      
                      sx={{ mt: 1, background: 'linear-gradient(to right, #8e2de2, #4a00e0)' }}
                      onClick={() => {
                        const crewFiles = extractFiles(crewDetails);
                        handleOpenModal('crew', crewFiles);
                      }}
                    >
                      CREW FILES
                    </Button>
                  </TableCell>
                  <TableCell>
                    {contract?.crewingAgent?.agentName}<br />
                    <Button
                      variant="contained"
                      sx={{ mt: 1 }}
                      onClick={() => {
                        const docFiles = extractDocFiles(contract);
                        handleOpenModal('docs', docFiles);
                      }}
                    >
                      DOCUMENTS
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <Typography>No contracts found</Typography>
      )}

      {renderModal()}
    </Box>
  );
};

export default BsmContracts;
